import { useEffect, useMemo, useState } from 'react';

import { marketSizeCalculation } from '../redux/actions/Firebase';
import { defaultFilterState, hasActiveFilters } from '../myComponents/filters/helpers';

const DEFAULT_MARKET_STATS = {
  filteredCount: null,
  totalCount: null,
  percentage: 100,
  formattedPercentage: '100.0',
  isLoading: false,
};

const formatPercentage = value => {
  if (Number.isNaN(value) || !Number.isFinite(value)) return '0.0';
  return Number(value).toFixed(1);
};

const getPercentage = (filteredCount, totalCount) => {
  if (!totalCount) return 0;
  return (filteredCount / totalCount) * 100;
};

const useMarketSizePercentage = ({ filters, batches, batchCountry, debounceMs = 200 }) => {
  const [marketStats, setMarketStats] = useState(DEFAULT_MARKET_STATS);

  const hasBatchContext = Boolean(batches?.year && Array.isArray(batches?.quarter) && batches.quarter.length && batchCountry);
  const filtersToUse = filters || defaultFilterState();
  const hasFiltersApplied = hasActiveFilters(filtersToUse);

  const requestPayload = useMemo(
    () => ({ filters: hasFiltersApplied ? filtersToUse : null, batches, batchCountry }),
    [filtersToUse, hasFiltersApplied, batches, batchCountry],
  );

  useEffect(() => {
    if (!hasBatchContext) {
      setMarketStats(DEFAULT_MARKET_STATS);
      return;
    }

    let isCancelled = false;

    setMarketStats(prevState => ({ ...prevState, isLoading: true }));

    const timeoutId = setTimeout(() => {
      marketSizeCalculation(requestPayload)
        .then(result => {
          if (isCancelled) return;

          const totalCount = Number(result?.totalCount || 0);
          const filteredCount = Number(result?.filteredCount || 0);

          const percentage = hasFiltersApplied ? getPercentage(filteredCount, totalCount) : totalCount ? 100 : 0;

          setMarketStats({
            filteredCount,
            totalCount,
            percentage,
            formattedPercentage: formatPercentage(percentage),
            isLoading: false,
          });
        })
        .catch(() => {
          if (isCancelled) return;

          setMarketStats({
            ...DEFAULT_MARKET_STATS,
            percentage: 0,
            formattedPercentage: '0.0',
            isLoading: false,
          });
        });
    }, debounceMs);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [requestPayload, hasBatchContext, hasFiltersApplied, debounceMs]);

  return marketStats;
};

export default useMarketSizePercentage;
